import type { HDType } from './types';
import { SYMPTOMS_BY_TYPE } from './types';

export interface EnergyCenter {
  name: string;
  description: string;
  symptoms: {
    balanced: string[];
    unbalanced: string[];
  };
  recommendations: string[];
}

export const ENERGY_CENTERS: Record<string, EnergyCenter> = {
  'crown': {
    name: 'Centre de la Couronne',
    description: 'Centre de la connexion spirituelle et de l\'inspiration',
    symptoms: {
      balanced: [
        'Clarté mentale',
        'Inspiration fluide',
        'Connexion spirituelle'
      ],
      unbalanced: [
        'Confusion mentale',
        'Surcharge d\'idées',
        'Déconnexion spirituelle'
      ]
    },
    recommendations: [
      'Pratiquer la méditation',
      'Créer des moments de silence',
      'Se connecter à la nature'
    ]
  },
  'ajna': {
    name: 'Centre Ajna',
    description: 'Centre de la vision et de l\'intuition',
    symptoms: {
      balanced: [
        'Vision claire',
        'Intuition forte',
        'Décisions aisées'
      ],
      unbalanced: [
        'Doutes persistants',
        'Confusion décisionnelle',
        'Mental surchargé'
      ]
    },
    recommendations: [
      'Tenir un journal',
      'Pratiquer la visualisation',
      'Faire des pauses régulières'
    ]
  },
  'throat': {
    name: 'Centre de la Gorge',
    description: 'Centre de l\'expression et de la communication',
    symptoms: {
      balanced: [
        'Expression fluide',
        'Communication claire',
        'Authenticité'
      ],
      unbalanced: [
        'Difficulté à s\'exprimer',
        'Tensions dans la gorge',
        'Frustration communicative'
      ]
    },
    recommendations: [
      'Pratiquer le chant',
      'Exprimer ses émotions',
      'Écrire régulièrement'
    ]
  },
  'heart': {
    name: 'Centre du Cœur',
    description: 'Centre de l\'amour et de la volonté',
    symptoms: {
      balanced: [
        'Amour de soi',
        'Force intérieure',
        'Volonté claire'
      ],
      unbalanced: [
        'Tensions émotionnelles',
        'Manque d\'estime',
        'Épuisement'
      ]
    },
    recommendations: [
      'Pratiquer l\'auto-compassion',
      'Cultiver la gratitude',
      'Équilibrer effort et repos'
    ]
  },
  'solar': {
    name: 'Plexus Solaire',
    description: 'Centre des émotions et de l\'intuition',
    symptoms: {
      balanced: [
        'Stabilité émotionnelle',
        'Intuition claire',
        'Confiance en soi'
      ],
      unbalanced: [
        'Anxiété',
        'Émotions instables',
        'Tensions digestives'
      ]
    },
    recommendations: [
      'Respiration consciente',
      'Exercice doux',
      'Expression émotionnelle'
    ]
  },
  'sacral': {
    name: 'Centre Sacral',
    description: 'Centre de la vitalité et de la créativité',
    symptoms: {
      balanced: [
        'Énergie stable',
        'Créativité fluide',
        'Vitalité'
      ],
      unbalanced: [
        'Fatigue chronique',
        'Blocages créatifs',
        'Tensions lombaires',
        'Douleurs bas du dos'
      ]
    },
    recommendations: [
      'Respecter ses cycles',
      'Activités créatives',
      'Mouvement régulier'
    ]
  },
  'root': {
    name: 'Centre Racine',
    description: 'Centre de la sécurité et de l\'ancrage',
    symptoms: {
      balanced: [
        'Stabilité',
        'Sécurité',
        'Ancrage'
      ],
      unbalanced: [
        'Stress',
        'Insécurité',
        'Déracinement',
        'Douleurs bas du dos'
      ]
    },
    recommendations: [
      'Marche consciente',
      'Routines stables',
      'Connexion à la terre'
    ]
  }
};

// Mapping des symptômes physiques aux centres affectés
const PHYSICAL_SYMPTOMS_MAPPING: Record<string, string[]> = {
  'dos': ['root', 'sacral'],
  'tension': ['throat', 'heart'],
  'douleur': ['root', 'sacral'],
  'fatigue': ['sacral', 'solar'],
  'lourdeur': ['root', 'sacral']
};

export const ENERGY_CENTERS_BY_TYPE: Record<HDType, string[]> = {
  'projector': ['ajna', 'heart', 'solar', 'throat'],
  'generator': ['sacral', 'root', 'solar', 'heart'],
  'manifesting-generator': ['sacral', 'throat', 'heart', 'solar'],
  'manifestor': ['crown', 'throat', 'heart', 'ajna'],
  'reflector': ['crown', 'ajna', 'heart', 'solar', 'root']
};

export function analyzeEnergyCenters(type: HDType, symptoms: string[]): {
  affectedCenters: string[];
  recommendations: string[];
} {
  const affectedCenters = new Set<string>();
  const recommendations = new Set<string>();

  // Si aucun symptôme n'est sélectionné, retourner des tableaux vides
  if (symptoms.length === 0) {
    return {
      affectedCenters: [],
      recommendations: []
    };
  }

  // Analyser chaque symptôme
  for (const symptom of symptoms) {
    const lowerSymptom = symptom.toLowerCase();
    let isPhysicalSymptom = false;

    // 1. Vérifier d'abord les symptômes physiques
    for (const [keyword, centers] of Object.entries(PHYSICAL_SYMPTOMS_MAPPING)) {
      if (lowerSymptom.includes(keyword)) {
        isPhysicalSymptom = true;
        centers.forEach(center => {
          affectedCenters.add(ENERGY_CENTERS[center].name);
          ENERGY_CENTERS[center].recommendations.forEach(rec => recommendations.add(rec));
        });
      }
    }

    // 2. Vérifier l'impact énergétique du symptôme
    const symptomObject = SYMPTOMS_BY_TYPE[type].find(s => s.text === symptom);
    if (symptomObject && symptomObject.energyImpact < 0) {
      // Pour les symptômes négatifs, ajouter les centres correspondants
      ENERGY_CENTERS_BY_TYPE[type].forEach(center => {
        if (ENERGY_CENTERS[center].symptoms.unbalanced.some(s => 
          lowerSymptom.includes(s.toLowerCase())
        )) {
          affectedCenters.add(ENERGY_CENTERS[center].name);
          ENERGY_CENTERS[center].recommendations.forEach(rec => recommendations.add(rec));
        }
      });
    }
  }

  // Si des centres sont affectés, retourner les résultats
  if (affectedCenters.size > 0) {
    return {
      affectedCenters: Array.from(affectedCenters),
      recommendations: Array.from(recommendations)
    };
  }

  // Si aucun centre n'est affecté, vérifier si tous les symptômes sont positifs
  const allPositive = symptoms.every(symptom => {
    const symptomObject = SYMPTOMS_BY_TYPE[type].find(s => s.text === symptom);
    return symptomObject && symptomObject.energyImpact > 0;
  });

  // Retourner des tableaux vides uniquement si tous les symptômes sont positifs
  return {
    affectedCenters: [],
    recommendations: []
  };
}